import { useMutation, useQuery } from '@tanstack/react-query';
import {
    fetchRescheduleTask,
    fetchRescheduleTeam,
    rescheduleErrorStatus,
    saveReschedule,
    type RescheduleCaller,
    type RescheduleGroup,
    type RescheduleRequest,
    type RescheduleTask,
    type RescheduleTeam
} from './reschedule-api';

export * from './reschedule-api';

/** Nested under one prefix (STYLE.md). A guest's read and a session's read are different entries. */
export const rescheduleQueryKeys = {
    all: ['reschedule'] as const,
    task: (orderId: number, taskId: number, caller: RescheduleCaller) =>
        ['reschedule', 'task', orderId, taskId, caller.downloadGuid ? 'guest' : 'session'] as const,
    team: (orderId: number, taskId: number, caller: RescheduleCaller) =>
        ['reschedule', 'team', orderId, taskId, caller.downloadGuid ? 'guest' : 'session'] as const
};

/**
 * The page's view of one task. `enabled` is the caller's "I know who is asking" — the page holds
 * the read back until the session check has finished, so a signed-in user is never read as a guest.
 */
export function useRescheduleTask(
    orderId: number,
    taskId: number,
    caller: RescheduleCaller,
    enabled = true
) {
    return useQuery<RescheduleTask, Error>({
        queryKey: rescheduleQueryKeys.task(orderId, taskId, caller),
        queryFn: () => fetchRescheduleTask(orderId, taskId, caller),
        enabled,
        // A 404 is the answer ("no schedulable appointment"), not a blip worth repeating.
        retry: (failureCount, error) =>
            rescheduleErrorStatus(error) === undefined && failureCount < 2,
        // The flags (`tooLate` above all) are a function of the clock; do not reuse an old read.
        staleTime: 0,
        refetchOnWindowFocus: false,
        throwOnError: false
    });
}

/**
 * Who may be chosen instead of the current photographer (ROBO-694). `enabled` is the task's
 * `canChoosePhotographer`: the route is not asked at all when the caller may not choose.
 *
 * Read once per visit and never refetched behind the user's back — the list decides which calendar
 * is on screen, and a silent change would drop the time they are holding.
 */
export function useRescheduleTeam(
    orderId: number,
    taskId: number,
    caller: RescheduleCaller,
    enabled = true
) {
    return useQuery<RescheduleTeam, Error>({
        queryKey: rescheduleQueryKeys.team(orderId, taskId, caller),
        queryFn: () => fetchRescheduleTeam(orderId, taskId, caller),
        enabled,
        retry: (failureCount, error) =>
            rescheduleErrorStatus(error) === undefined && failureCount < 2,
        staleTime: Infinity,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        throwOnError: false
    });
}

/**
 * The save. Rejects with the `HttpError` the API produced, so the page can tell a 409 (the time
 * has gone — pick again) from a 400 (the API's own message) from a 404.
 */
export function useRescheduleSave(orderId: number, taskId: number, caller: RescheduleCaller) {
    return useMutation<RescheduleGroup[], Error, RescheduleRequest>({
        mutationFn: body => saveReschedule(orderId, taskId, body, caller)
    });
}
